import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { playStoreUrl, playBadgeSrc } from '@/utils/playStore';
import { brandLogoSrc } from '@/utils/brand';
import { isRtl } from '@/i18n/config';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Menu, User, UserPlus, LogOut, FileText, MessageCircle, Home, Search, Mail, Star, UserCircle, Settings, Shield, LogIn, Plus, Facebook, Instagram, Send, Phone } from 'lucide-react';
import SupportChatWidget from '@/components/SupportChatWidget';
import InstallAppBanner from '@/components/InstallAppBanner';
import NotificationBell from '@/components/NotificationBell';
import MessageBell from '@/components/MessageBell';
import LanguageToggle from '@/components/LanguageToggle';
import DisplayNamePrompt from '@/components/DisplayNamePrompt';

const Layout = () => {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);

  // Show WhatsApp button on home and FAQ pages
  const showSupportChat = ['/', '/faq'].includes(location.pathname);

  // Chat-style routes use a locked-viewport shell (no footer, no body scroll)
  // — this is the standard 2026 pattern for messaging apps:
  //   Slack, Discord, WhatsApp Web, Gmail, Twitter DMs all do this.
  // The marketing footer is for landing/info pages, not functional UIs where
  // it would steal vertical space the user needs for the conversation.
  const isChatRoute = location.pathname.startsWith('/messages');

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { href: '/', label: t('nav.home'), icon: Home },
    { href: '/browse', label: t('nav.browseProfiles'), icon: Search },
    { href: '/faq', label: t('nav.howToUse'), icon: FileText },
    { href: '/contact', label: t('nav.contact'), icon: Phone },
  ];

  return (
    // Chat routes: lock to dynamic viewport height with overflow-hidden so the
    // chat surface inside `<main>` can use h-full without the page scrolling.
    // Marketing routes: standard min-h-screen with grow-as-needed content.
    <div className={
      isChatRoute
        ? 'h-[100dvh] flex flex-col overflow-hidden'
        : 'min-h-screen flex flex-col'
    }>
      {/* Skip to content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:end-4 focus:z-[100] focus:bg-wefaqna-forest focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:font-bold focus:shadow-lg"
      >
        {t('nav.skipToContent')}
      </a>

      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-wefaqna-green/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 relative">
            {/* Logo - Centered on mobile, right-aligned on desktop */}
            <Link
              to="/"
              className="flex items-center gap-2 absolute left-1/2 -translate-x-1/2 md:relative md:left-0 md:translate-x-0"
              data-testid="header-logo"
            >
              {logoError ? (
                <span className="text-xl md:text-2xl font-bold text-wefaqna-navy">{t('common.appName')}</span>
              ) : (
                <img
                  src={brandLogoSrc(i18n.language)}
                  alt={t('common.appName')}
                  className="h-8 md:h-10 w-auto transform scale-110 md:scale-150 origin-center"
                  onError={() => setLogoError(true)}
                />
              )}
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-wefaqna-navy hover:text-wefaqna-forest transition-colors font-medium"
                  data-testid={`nav-${link.href.replace('/', '') || 'home'}`}
                >
                  {link.label}
                </Link>
              ))}
              {/* Only for a member with no profile yet. With one, the button
                  bounced her to the dashboard with "you already have a
                  profile" (BUG-523). */}
              {isAuthenticated && user?.profile_preference && user?.has_form !== true && (
                <Button
                  className="flex items-center gap-2 bg-wefaqna-forest text-white hover:bg-wefaqna-forest-hover font-medium rounded-full px-4"
                  onClick={() => navigate(user.profile_preference === 'groom' ? '/groom-form' : '/bride-form')}
                >
                  <Plus className="h-4 w-4" />
                  {user.profile_preference === 'groom' ? t('nav.createGroomProfile') : t('nav.createBrideProfile')}
                </Button>
              )}
            </nav>

            {/* Desktop Auth Section */}
            <div className="hidden md:flex items-center gap-2">
              {/* Language switcher — outside the auth branches so it is present
                  for guests and signed-in users alike. Diaspora traffic lands
                  on an Arabic-by-default site and needs a visible way out. */}
              <LanguageToggle variant="compact" />
              {loading ? (
                /* Show skeleton while checking auth */
                <Skeleton className="h-10 w-10 rounded-full" />
              ) : isAuthenticated ? (
                /* User Menu when authenticated */
                <>
                  {/* Dashboard Icon */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full relative"
                    onClick={() => navigate('/dashboard')}
                    aria-label={t('nav.dashboardAria')}
                    title={t('nav.dashboardAria')}
                  >
                    <Home className="h-5 w-5" />
                  </Button>
                  {/* Messages Dropdown */}
                  <MessageBell />
                  <NotificationBell />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="rounded-full" aria-label={t('nav.userMenuAria')}>
                        <User className="h-5 w-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <div className="px-2 py-1.5">
                        <p className="text-sm font-medium text-wefaqna-navy">{user?.name || t('userMenu.fallbackName')}</p>
                        <p className="text-xs text-gray-500">{user?.email || t('userMenu.fallbackEmailTag')}</p>
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/settings')}>
                        <Settings className="ms-2 h-4 w-4" />
                        <span>{t('settings.title')}</span>
                      </DropdownMenuItem>
                      {user?.is_admin && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => navigate('/admin/dashboard')} className="text-purple-600">
                            <Shield className="ms-2 h-4 w-4" />
                            <span>{t('userMenu.adminPanel')}</span>
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                        <LogOut className="ms-2 h-4 w-4" />
                        <span>{t('userMenu.logout')}</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                /* Auth Buttons for non-authenticated users */
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => navigate('/register')}
                    className="bg-wefaqna-forest hover:bg-wefaqna-forest-hover text-white font-bold px-5 py-2 rounded-xl transition-all duration-300"
                    data-testid="desktop-register-btn"
                  >
                    <UserPlus className="h-5 w-5 ms-2" />
                    {t('nav.register')}
                  </Button>
                  <Button
                    onClick={() => navigate('/login')}
                    variant="outline"
                    className="border-2 border-wefaqna-navy/30 text-wefaqna-navy font-bold px-5 py-2 rounded-xl transition-all duration-300 hover:bg-wefaqna-navy/5"
                    data-testid="desktop-signin-btn"
                  >
                    <LogIn className="h-5 w-5 ms-2" />
                    {t('nav.login')}
                  </Button>
                </div>
              )}
            </div>

            {/* Mobile Menu - RTL: first item goes RIGHT, last item goes LEFT */}
            <div className="flex md:hidden items-center justify-between w-full">
              {/* Right side (first in RTL): notifications for auth, sign-in for guests */}
              {!loading && (isAuthenticated ? (
                <div className="flex items-center gap-0.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-full h-9 w-9 p-0"
                    onClick={() => navigate('/dashboard')}
                    aria-label={t('nav.dashboardAria')}
                  >
                    <Home className="h-4 w-4" />
                  </Button>
                  <MessageBell />
                  <NotificationBell />
                </div>
              ) : (
                <Button
                  onClick={() => navigate('/login')}
                  variant="outline"
                  size="sm"
                  className="h-9 px-3 rounded-full border-2 border-wefaqna-navy/30 text-wefaqna-navy font-bold text-sm hover:bg-wefaqna-navy/5"
                  data-testid="mobile-header-signin-btn"
                  aria-label={t('nav.loginAria')}
                >
                  <LogIn className="h-4 w-4 ms-1" />
                  {t('nav.login')}
                </Button>
              ))}
              {/* Menu button on LEFT (last in RTL) */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="mobile-menu-btn"
                    aria-label={t('nav.openMenuAria')}
                    className="rounded-full h-9 w-9 p-0"
                  >
                    <Menu className="h-5 w-5" />
                    <span className="sr-only">{t('nav.menu')}</span>
                  </Button>
                </SheetTrigger>
                {/* `side` is PHYSICAL, so it has to follow the direction by hand:
                    the trigger sits last in a justify-between row, which puts it
                    on the left in RTL and on the right in LTR. Hardcoding "left"
                    made the English sheet fly in from the opposite edge to the
                    button that opened it. */}
                <SheetContent side={isRtl(i18n.language) ? 'left' : 'right'} className="w-72 pt-12">
                  <SheetTitle className="text-wefaqna-navy mb-4 text-end pe-2">{t('userMenu.sheetTitle')}</SheetTitle>
                  <nav className="flex flex-col gap-2 mt-2">
                    {/* Language switcher. The mobile header row is already
                        justify-between with an absolutely-centred logo between
                        the two icon clusters, so a third cluster there risks
                        overlapping the logo on small phones — it lives in the
                        sheet instead, where it gets full-width 48dp targets.
                        Sits first so a visitor who cannot read the Arabic nav
                        labels reaches it without scanning past them - as one
                        row that opens the list, not a grid of eight. */}
                    <div className="pb-2 mb-1 border-b border-gray-200">
                      <LanguageToggle
                        variant="full"
                        onChanged={() => setMobileMenuOpen(false)}
                      />
                    </div>

                    {navLinks.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        className="flex items-center gap-3 text-wefaqna-navy hover:text-wefaqna-forest transition-colors font-medium p-3 rounded-lg hover:bg-wefaqna-subtle min-h-[48px] touch-manipulation"
                        onClick={() => setMobileMenuOpen(false)}
                        data-testid={`mobile-nav-${link.href.replace('/', '') || 'home'}`}
                      >
                        <link.icon className="h-5 w-5" />
                        {link.label}
                      </Link>
                    ))}

                    {/* Auth Section in Mobile Menu */}
                    {loading ? (
                      /* Show skeleton while checking auth */
                      <div className="mt-4 space-y-2">
                        <Skeleton className="h-10 w-full rounded-xl" />
                      </div>
                    ) : !isAuthenticated ? (
                      /* Auth Buttons for non-authenticated users */
                      <div className="mt-2 space-y-2">
                        <Button
                          onClick={() => {
                            setMobileMenuOpen(false);
                            navigate('/register');
                          }}
                          className="bg-wefaqna-forest hover:bg-wefaqna-forest-hover text-white font-bold rounded-xl w-full"
                          data-testid="mobile-register-btn"
                        >
                          <UserPlus className="h-5 w-5 ms-2" />
                          {t('nav.register')}
                        </Button>
                        <Button
                          onClick={() => {
                            setMobileMenuOpen(false);
                            navigate('/login');
                          }}
                          variant="outline"
                          className="border-2 border-wefaqna-navy/30 text-wefaqna-navy font-bold rounded-xl w-full"
                          data-testid="mobile-signin-btn"
                        >
                          <LogIn className="h-5 w-5 ms-2" />
                          {t('nav.login')}
                        </Button>
                      </div>
                    ) : null}

                    {!loading && isAuthenticated && (
                      <>
                        {user?.profile_preference && user?.has_form !== true && (
                          <div className="border-t border-gray-200 my-2 pt-2">
                            <Link
                              to={user.profile_preference === 'groom' ? '/groom-form' : '/bride-form'}
                              className="flex items-center gap-3 text-wefaqna-forest hover:text-wefaqna-forest-hover transition-colors font-medium p-3 rounded-lg hover:bg-wefaqna-subtle min-h-[48px] touch-manipulation"
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              <Plus className="h-5 w-5" />
                              {user.profile_preference === 'groom' ? t('nav.createGroomProfile') : t('nav.createBrideProfile')}
                            </Link>
                          </div>
                        )}
                        <div className="border-t border-gray-200 my-2 pt-2">
                          <Link
                            to="/settings"
                            className="flex items-center gap-3 text-wefaqna-navy hover:text-wefaqna-forest transition-colors font-medium p-3 rounded-lg hover:bg-wefaqna-subtle min-h-[48px] touch-manipulation"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <Settings className="h-5 w-5" />
                            {t('settings.title')}
                          </Link>
                          <Link
                            to="/messages"
                            className="flex items-center gap-3 text-wefaqna-forest hover:text-wefaqna-forest-hover transition-colors font-medium p-3 rounded-lg hover:bg-wefaqna-subtle min-h-[48px] touch-manipulation"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <MessageCircle className="h-5 w-5" />
                            {t('nav.messages')}
                          </Link>
                        </div>
                        {user?.is_admin && (
                          <Link
                            to="/admin/dashboard"
                            className="flex items-center gap-3 text-purple-600 hover:text-purple-700 transition-colors font-medium p-3 rounded-lg hover:bg-purple-50"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <Shield className="h-5 w-5" />
                            {t('userMenu.adminPanel')}
                          </Link>
                        )}
                        <button
                          onClick={() => {
                            setMobileMenuOpen(false);
                            handleLogout();
                          }}
                          className="flex items-center gap-3 text-red-600 hover:text-red-700 transition-colors font-medium p-3 rounded-lg hover:bg-red-50 text-end w-full"
                        >
                          <LogOut className="h-5 w-5" />
                          {t('userMenu.logout')}
                        </button>
                      </>
                    )}
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      {/* On chat routes, main is overflow-hidden — the chat itself manages */}
      {/* its own internal scrolling (message list scrolls, header + input  */}
      {/* stay anchored). This prevents body scroll and footer overlap.     */}
      <main
        id="main-content"
        className={isChatRoute ? 'flex-1 min-h-0 overflow-hidden' : 'flex-1'}
        role="main"
        tabIndex={-1}
      >
        <Outlet />
      </main>

      {/* Footer — hidden on chat routes (matches Slack/Discord/WhatsApp web) */}
      {!isChatRoute && (
      <footer className="bg-wefaqna-navy text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center md:text-end">
            {/* About */}
            <div>
              <h3 className="text-xl font-bold mb-4 text-wefaqna-green">{t('userMenu.footerAbout')}</h3>
              <ul className="space-y-2 text-gray-300">
                <li><Link to="/about#vision" className="hover:text-wefaqna-green transition-colors">{t('nav.vision')}</Link></li>
                <li><Link to="/about#mission" className="hover:text-wefaqna-green transition-colors">{t('nav.mission')}</Link></li>
                <li><Link to="/about#values" className="hover:text-wefaqna-green transition-colors">{t('nav.values')}</Link></li>
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-xl font-bold mb-4 text-wefaqna-green">{t('userMenu.footerQuickLinks')}</h3>
              <ul className="space-y-2 text-gray-300">
                <li><Link to="/" className="hover:text-wefaqna-green transition-colors">{t('nav.home')}</Link></li>
                <li><Link to="/browse" className="hover:text-wefaqna-green transition-colors">{t('nav.browseProfiles')}</Link></li>
                <li><Link to="/faq" className="hover:text-wefaqna-green transition-colors">{t('nav.howToUse')}</Link></li>
                <li><Link to="/faq" className="hover:text-wefaqna-green transition-colors">{t('nav.faq')}</Link></li>
              </ul>
            </div>

            {/* Policies */}
            <div>
              <h3 className="text-xl font-bold mb-4 text-wefaqna-green">{t('userMenu.footerLegal')}</h3>
              <ul className="space-y-2 text-gray-300">
                <li><Link to="/privacy" className="hover:text-wefaqna-green transition-colors">{t('nav.privacy')}</Link></li>
                <li><Link to="/terms" className="hover:text-wefaqna-green transition-colors">{t('nav.terms')}</Link></li>
                <li><Link to="/data-deletion" className="hover:text-wefaqna-green transition-colors">{t('nav.dataDeletion')}</Link></li>
                <li><Link to="/complaint" className="hover:text-wefaqna-green transition-colors">{t('userMenu.reportPolicy')}</Link></li>
                <li><Link to="/child-safety" className="hover:text-wefaqna-green transition-colors">{t('footer.childSafety')}</Link></li>
                <li><Link to="/impressum" className="hover:text-wefaqna-green transition-colors">{t('footer.impressum')}</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-xl font-bold mb-4 text-wefaqna-green">{t('footer.support')}</h3>
              <ul className="space-y-2 text-gray-300">
                <li><Link to="/contact" className="hover:text-wefaqna-green transition-colors">{t('nav.contact')}</Link></li>
                <li><Link to="/complaint" className="hover:text-wefaqna-green transition-colors">{t('nav.complaintsAndSuggestions')}</Link></li>
                <li><Link to="/faq" className="hover:text-wefaqna-green transition-colors">{t('nav.helpCenter')}</Link></li>
              </ul>
              <div className="mt-4 text-gray-300">
                <p className="flex items-center justify-center md:justify-start gap-2">
                  <Mail className="h-4 w-4 text-wefaqna-green" />
                  info@wefaqna.com
                </p>
                <p className="mt-2">{t('contact.address')}</p>
              </div>

              {/* Social Media Links */}
              <div className="mt-6">
                <h4 className="text-sm font-semibold mb-3 text-wefaqna-green">{t('footer.followUs')}</h4>
                <div className="flex items-center justify-center md:justify-start gap-4">
                  <a
                    href="https://whatsapp.com/channel/0029VbCa0iZJkK7FjKLvm637"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-300 hover:text-wefaqna-green transition-colors"
                    aria-label={t('footer.followWhatsappAria')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
                    </svg>
                  </a>
                  <a
                    href="https://www.facebook.com/share/1Euqk2SH4b/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-300 hover:text-wefaqna-green transition-colors"
                    aria-label={t('footer.followFacebookAria')}
                  >
                    <Facebook className="h-6 w-6" />
                  </a>
                  <a
                    href="https://www.instagram.com/wefaqna"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-300 hover:text-wefaqna-green transition-colors"
                    aria-label={t('footer.followInstagramAria')}
                  >
                    <Instagram className="h-6 w-6" />
                  </a>
                  <a
                    href="https://t.me/wefaqna"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-300 hover:text-wefaqna-green transition-colors"
                    aria-label={t('footer.followTelegramAria')}
                  >
                    <Send className="h-6 w-6" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Download App CTA */}
          <div className="mt-12 pt-8 border-t border-gray-700">
            <div className="flex flex-col items-center gap-4">
              <h3 className="text-lg font-bold text-white">{t('footer.downloadAppHeading')}</h3>
              <a
                href={playStoreUrl({ medium: 'site_footer' })}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block hover:scale-[1.03] active:scale-[0.98] transition-transform duration-200"
                data-testid="footer-download-app-btn"
                aria-label={t('footer.downloadAppAria')}
              >
                <img
                  src={playBadgeSrc(i18n.language)}
                  alt={t('footer.downloadAppAlt')}
                  width={180}
                  height={54}
                  className="h-14 w-auto"
                />
              </a>
              <div className="hidden sm:flex flex-col items-center gap-2">
                <p className="text-sm text-gray-300">{t('footer.scanQrCode')}</p>
                <img
                  src="/play-store-qr.png"
                  alt="Google Play QR code for Wefaqna"
                  width={120}
                  height={120}
                  className="bg-white p-2 rounded-lg"
                />
              </div>
              <p className="text-xs text-gray-400">{t('footer.iosSoon')}</p>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-8 pt-6 border-t border-gray-700 text-center text-gray-400">
            <p>{t('footer.copyright', { year: new Date().getFullYear() })}</p>
            <p className="mt-2 text-sm">{t('footer.complianceTagline')}</p>
          </div>
        </div>
      </footer>
      )}

      {/* Live Support Chat Widget — only on landing/browse pages */}
      {showSupportChat && <SupportChatWidget />}

      {/* Sticky install banner — mobile-web only, anonymous only,
          public pages only. See InstallAppBanner for the rules. */}
      <InstallAppBanner />

      {/* Asks an older profile, softly, for the name others will see. */}
      <DisplayNamePrompt />
    </div>
  );
};

export default Layout;
